import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { X, ChevronLeft, ChevronRight, Loader2, AlertCircle, Lock } from 'lucide-react'
import { format, parseISO, addDays, startOfMonth, endOfMonth } from 'date-fns'
import {
  getAvailabilityDay,
  getAvailabilityGrid,
  updateAvailability,
  type AvailabilityDayItem,
  type AvailabilityInfo,
  type AvailabilityOverride,
} from '../../lib/api/client'
import { cn } from '../../lib/cn'

type RowState = { newQuantity: string; closed: boolean }
const keyOf = (i: AvailabilityDayItem) => `${i.referenceType}:${i.referenceId}`

const UNLIMITED = 9999 // baseline "no cap"; 9999 / -999 are sentinels, never a real capacity
const isSane = (n: number | null | undefined) => typeof n === 'number' && n > 0 && n < UNLIMITED && n !== -999
const fmtNum = (n: number) => (n >= UNLIMITED ? '∞' : String(n))
// first non-sentinel value across all sources, else "unlimited"
const saneMax = (...vals: (number | null | undefined)[]): number => {
  for (const v of vals) if (isSane(v)) return v as number
  return UNLIMITED
}

// The per-day modal endpoint resolves caps wrong (its single-day query misses the availability
// record; on-site comes back as the 9999 baseline). We pick the first sane value across: the
// month grid (correct for parking), the day override, and the configured default (correct for on-site).
function gridMatch(grid: AvailabilityInfo[] | undefined, i: AvailabilityDayItem): AvailabilityInfo | undefined {
  if (!grid) return undefined
  if (i.referenceType === 'SERVICE') return grid.find((g) => g.type === 'SERVICE' && g.id === i.referenceId)
  // both "Tipi di veicoli" and "Pagamenti in cassa" map to the transport grid item
  return grid.find((g) => g.type === 'TRANSPORT' && g.id === i.referenceId)
}
function maxOf(i: AvailabilityDayItem, grid?: AvailabilityInfo[]): number {
  const g = gridMatch(grid, i)
  const gridVal = i.referenceType === 'MAXONSITEPAYMENTAVAILABILITY' ? g?.maxOnSite : g?.maxAllowed
  return saneMax(gridVal, i.quantity, i.defaultQuantity)
}
function occupiedOf(i: AvailabilityDayItem, grid?: AvailabilityInfo[]): number {
  const g = gridMatch(grid, i)
  if (i.referenceType === 'TRANSPORT') return g?.occupiedParkingSpaces ?? 0
  if (i.referenceType === 'MAXONSITEPAYMENTAVAILABILITY') return g?.book && typeof g.book === 'object' ? g.book.onsite : 0
  if (i.referenceType === 'SERVICE') return typeof g?.book === 'number' ? g.book : 0
  return 0
}
// "Disponibilità" (remaining) = effective max − occupied
function availOf(i: AvailabilityDayItem, grid?: AvailabilityInfo[]): number {
  return maxOf(i, grid) - occupiedOf(i, grid)
}

export function DayModal({
  date,
  preferLabel,
  onClose,
  onNavigate,
}: {
  date: string | null
  preferLabel?: string
  onClose: () => void
  onNavigate: (date: string) => void
}) {
  const qc = useQueryClient()
  const [tabIdx, setTabIdx] = useState(0)
  const [edits, setEdits] = useState<Record<string, RowState>>({})
  const [saving, setSaving] = useState(false)

  const day = useQuery({
    queryKey: ['availabilityDay', date],
    queryFn: () => getAvailabilityDay(date!),
    enabled: Boolean(date),
  })

  // Effective caps come from the month grid (correct), not the single-day report. Keyed by the
  // month range so it reuses the page's cached grid when the open day is in the visible month.
  const monthStartStr = date ? format(startOfMonth(parseISO(date)), 'yyyy-MM-dd') : ''
  const monthEndStr = date ? format(endOfMonth(parseISO(date)), 'yyyy-MM-dd') : ''
  const monthGrid = useQuery({
    queryKey: ['availabilityGrid', monthStartStr, monthEndStr],
    queryFn: () => getAvailabilityGrid(monthStartStr, monthEndStr),
    enabled: Boolean(date),
  })
  const gridInfo = date ? monthGrid.data?.[date]?.info : undefined

  const todayStr = format(new Date(), 'yyyy-MM-dd')
  const disableEdit = !date || date < todayStr

  // seed edit state whenever a new day's data arrives
  useEffect(() => {
    if (!day.data) return
    const seed: Record<string, RowState> = {}
    for (const tab of day.data) for (const it of tab.items) seed[keyOf(it)] = { newQuantity: '', closed: it.closed }
    setEdits(seed)
    const pref = preferLabel ? day.data.findIndex((t) => t.label === preferLabel) : -1
    setTabIdx(pref >= 0 ? pref : 0)
  }, [day.data, preferLabel])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    if (date) document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [date, onClose])

  const allItems = useMemo(() => (day.data ?? []).flatMap((t) => t.items), [day.data])

  if (!date) return null

  const setRow = (k: string, patch: Partial<RowState>) =>
    setEdits((e) => ({ ...e, [k]: { ...e[k], ...patch } }))

  async function save() {
    if (disableEdit) return
    const overrides: AvailabilityOverride[] = allItems.map((it) => {
      const k = keyOf(it)
      const st = edits[k] ?? { newQuantity: '', closed: it.closed }
      return {
        referenceId: it.referenceId,
        referenceType: it.referenceType,
        date: date!,
        newQuantity: st.newQuantity,
        currentQuantity: maxOf(it, gridInfo),
        defaultQuantity: it.defaultQuantity,
        closed: st.closed,
      }
    })
    setSaving(true)
    try {
      await updateAvailability(overrides)
      await qc.invalidateQueries({ queryKey: ['availabilityGrid'] })
      await qc.invalidateQueries({ queryKey: ['availabilityDay'] })
      onClose()
    } catch {
      // surface minimal feedback; keep the modal open so edits aren't lost
      alert('Salvataggio non riuscito. Riprova.')
    } finally {
      setSaving(false)
    }
  }

  const tabs = day.data ?? []
  const activeTab = tabs[tabIdx]
  const headDate = format(parseISO(date), 'dd/MM/yyyy')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-coal/40 backdrop-blur-[1px] animate-[fadeIn_.15s_ease]" onClick={onClose} />
      <div className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-card border border-line bg-paper shadow-pop">
        {/* header */}
        <header className="flex items-center gap-3 border-b border-line bg-card px-5 py-3.5">
          <div className="flex items-center gap-1">
            <button
              onClick={() => onNavigate(format(addDays(parseISO(date), -1), 'yyyy-MM-dd'))}
              className="rounded-md p-1 text-muted hover:bg-paper hover:text-coal"
              title="Giorno precedente"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[96px] text-center font-display text-[16px] font-bold text-coal">{headDate}</span>
            <button
              onClick={() => onNavigate(format(addDays(parseISO(date), 1), 'yyyy-MM-dd'))}
              className="rounded-md p-1 text-muted hover:bg-paper hover:text-coal"
              title="Giorno successivo"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {disableEdit && (
            <span className="inline-flex items-center gap-1 rounded-full bg-paper px-2 py-0.5 text-[11px] font-semibold text-muted ring-1 ring-line">
              <Lock className="h-3 w-3" /> sola lettura
            </span>
          )}
          <button onClick={onClose} className="-mr-1 ml-auto rounded-lg p-1.5 text-muted hover:bg-paper hover:text-coal">
            <X className="h-[18px] w-[18px]" />
          </button>
        </header>

        {/* tabs */}
        {tabs.length > 0 && (
          <div className="flex gap-1 border-b border-line bg-card px-4">
            {tabs.map((t, i) => (
              <button
                key={t.label}
                onClick={() => setTabIdx(i)}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2.5 text-[13px] font-semibold transition-colors',
                  i === tabIdx ? 'border-verdigris text-coal' : 'border-transparent text-muted hover:text-coal',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {day.isLoading ? (
            <div className="flex items-center justify-center py-16 text-muted">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : day.isError ? (
            <div className="py-16 text-center text-sm text-muted">
              <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare il giorno.
            </div>
          ) : !activeTab ? (
            <p className="py-12 text-center text-sm text-muted">Nessun dato per questo giorno.</p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line text-left text-[11px] font-bold uppercase tracking-[1px] text-muted">
                  <th className="py-2 pr-3">Nome</th>
                  <th className="px-3 py-2 text-right">Disp.</th>
                  <th className="px-3 py-2 text-right">Max</th>
                  <th className="px-3 py-2 text-right">Nuova Max</th>
                  <th className="py-2 pl-3 text-right">Chiusa</th>
                </tr>
              </thead>
              <tbody>
                {activeTab.items.map((it) => {
                  const k = keyOf(it)
                  const st = edits[k] ?? { newQuantity: '', closed: it.closed }
                  return (
                    <tr key={k} className="border-b border-line-soft last:border-0">
                      <td className="py-2.5 pr-3 font-semibold text-coal">{it.label}</td>
                      <td className="px-3 py-2.5 text-right font-mono tnum text-slate">{fmtNum(availOf(it, gridInfo))}</td>
                      <td className="px-3 py-2.5 text-right">
                        <span className="font-mono tnum text-coal">{fmtNum(maxOf(it, gridInfo))}</span>
                        {isSane(it.defaultQuantity) && it.defaultQuantity !== maxOf(it, gridInfo) && (
                          <span className="ml-1 text-[11px] text-faint">(def. {it.defaultQuantity})</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <input
                          type="number"
                          min={0}
                          inputMode="numeric"
                          disabled={disableEdit || st.closed}
                          value={st.newQuantity}
                          onChange={(e) => setRow(k, { newQuantity: e.target.value })}
                          placeholder={maxOf(it, gridInfo) >= UNLIMITED ? '∞' : String(maxOf(it, gridInfo))}
                          className="h-8 w-20 rounded-md border border-line bg-card px-2 text-right font-mono text-[13px] text-coal outline-none focus:border-verdigris disabled:bg-paper disabled:text-faint"
                        />
                      </td>
                      <td className="py-2.5 pl-3 text-right">
                        <input
                          type="checkbox"
                          checked={st.closed}
                          disabled={disableEdit}
                          onChange={(e) => setRow(k, { closed: e.target.checked })}
                          className="h-4 w-4 accent-verdigris"
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* footer */}
        <footer className="flex items-center gap-2 border-t border-line bg-card px-5 py-3">
          <p className="text-[12px] text-muted">
            {disableEdit ? 'I giorni passati non sono modificabili.' : 'Lascia “Nuova Max” vuoto per non cambiare la capienza.'}
          </p>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={onClose}
              className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint"
            >
              {disableEdit ? 'Chiudi' : 'Annulla'}
            </button>
            {!disableEdit && (
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-2 text-[13px] font-semibold text-white hover:bg-verdigris-700 disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Salva
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  )
}
